import { Outlet, Link, NavLink } from 'react-router';
import { useAuth } from '@/context/AuthContext';
import { ROLE_LABEL } from '@/lib/constants';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  SparklesIcon,
  Menu01Icon,
  Cancel01Icon,
  ChartHistogramIcon,
  AiInnovation01Icon,
  Task01Icon,
  SecurityCheckIcon,
  Radar01Icon,
  BalanceScaleIcon,
  Settings02Icon,
  WorkflowCircle06Icon,
  InboxIcon,
  UserMultipleIcon,
  Alert01Icon,
  Calendar03Icon,
  ProfitIcon,
  BookOpen01Icon,
  DatabaseIcon,
} from '@hugeicons/core-free-icons';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { CommandSearch } from '@/components/shared/CommandSearch';
import { InstitutionalBanner, ViaBrandLockup } from '@/components/brand/InstitutionalBrand';
import { cn } from '@/lib/utils';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';

// Rutas de contenido informativo de libre acceso para cualquier usuario
// autenticado. No dependen de permisos del backend porque no exponen datos
// sensibles; son material de habilitación disponible para todos los equipos.
const PUBLIC_ROUTES = ['/guia-desarrollo'];

const FLYOUT_PORTFOLIO = [
  { to: '/dashboard', label: 'Panel ejecutivo', desc: 'Métricas y KPIs del portafolio', icon: ChartHistogramIcon },
  { to: '/demanda', label: 'Bandeja de demanda', desc: 'Intakes, señales tempranas y conversión', icon: InboxIcon },
  {
    to: '/registro',
    label: 'Iniciativas',
    desc: 'Registro, roadmap, tracking y priorización',
    icon: AiInnovation01Icon,
  },
  {
    to: '/capacidades',
    label: 'Capacidades IA',
    desc: 'Mapa de capacidades reutilizables',
    icon: WorkflowCircle06Icon,
  },
  { to: '/relaciones-negocio', label: 'Relaciones', desc: 'Contacto activo con el negocio', icon: UserMultipleIcon },
  { to: '/alertas-proveedor', label: 'Alertas proveedor', desc: 'Detección de contacto prematuro', icon: Alert01Icon },
  { to: '/agenda-gobierno', label: 'Agenda', desc: 'Cadencia de gobierno semanal/quincenal', icon: Calendar03Icon },
  { to: '/tareas', label: 'Mis tareas', desc: 'Tareas pendientes asignadas', icon: Task01Icon },
];

// Grupo "Valor y adopción": el flujo de medir y cosechar el valor de la IA,
// más los recursos de habilitación. Reúne lo financiero, el valor en producción,
// la cosecha de habilitaciones y la academia/recursos.
const FLYOUT_VALUE = [
  {
    to: '/insights',
    label: 'VIA Insights',
    desc: 'Pregunta a los datos en lenguaje natural',
    icon: AiInnovation01Icon,
  },
  { to: '/financiero', label: 'P&L Ejecutivo', desc: 'Posición financiera del portafolio', icon: ProfitIcon },
  { to: '/valor', label: 'Valor', desc: 'Monitoreo de valor en producción', icon: ChartHistogramIcon },
  {
    to: '/cosecha',
    label: 'Cosecha de Valor',
    desc: 'Habilitaciones, uso real y valor cosechado',
    icon: UserMultipleIcon,
  },
  {
    to: '/adopcion',
    label: 'Academia VIA',
    desc: 'Rutas por rol, certificación y comunidad',
    icon: AiInnovation01Icon,
  },
  {
    to: '/guia-desarrollo',
    label: 'Guía de desarrollo',
    desc: 'Construir sobre VIA: repos base, servicios y ruta',
    icon: BookOpen01Icon,
  },
  { to: '/kb', label: 'Knowledge Hub', desc: 'Guías, prompts y buenas prácticas', icon: SparklesIcon },
  {
    to: '/toolkit/prompts',
    label: 'Toolkit y catálogo IA',
    desc: 'Recursos colaborativos y activos técnicos',
    icon: WorkflowCircle06Icon,
  },
  { to: '/sandbox', label: 'Sandbox seguro', desc: 'Experimentación sintética y fast lane', icon: SecurityCheckIcon },
  {
    to: '/herramientas',
    label: 'Herramientas autorizadas',
    desc: 'Catálogo, permisos y uso permitido',
    icon: Settings02Icon,
  },
  { to: '/brand', label: 'Identidad UC CHRISTUS', desc: 'Logos, colores, tipografía y activos', icon: SparklesIcon },
];

const FLYOUT_GOVERNANCE = [
  { to: '/comite', label: 'Comité de IA', desc: 'Decisiones y sesiones del comité', icon: BalanceScaleIcon },
  { to: '/comite/configuracion', label: 'Config. Comité', desc: 'Miembros, quórum y parámetros', icon: Settings02Icon },
  { to: '/shadow-ai', label: 'Shadow AI', desc: 'Detección de uso no autorizado', icon: Radar01Icon },
  { to: '/reevaluaciones', label: 'Reevaluaciones', desc: 'Revisiones periódicas de riesgo', icon: SecurityCheckIcon },
  { to: '/incidentes', label: 'Incidentes', desc: 'SLA, contención, causa raíz y evidencia', icon: Radar01Icon },
  {
    to: '/vigilancia',
    label: 'Vigilancia continua',
    desc: 'Calidad, sesgo, deriva y suspensión',
    icon: SecurityCheckIcon,
  },
  {
    to: '/madurez',
    label: 'Madurez y hoja de ruta',
    desc: 'Capacidades, evidencia e integraciones',
    icon: ChartHistogramIcon,
  },
];

const FLYOUT_ADMIN = [
  {
    to: '/insights/agents',
    label: 'VIA Insights · Agentes',
    desc: 'Configura agentes: datos, descripciones e instrucciones',
    icon: DatabaseIcon,
  },
  {
    to: '/aplicaciones-ia',
    label: 'Gestión de Aplicaciones',
    desc: 'Aplicaciones, ambientes, bases de datos y accesos',
    icon: WorkflowCircle06Icon,
  },
  { to: '/accesos', label: 'Usuarios y accesos', desc: 'Usuarios, roles, perfiles y ámbitos', icon: UserMultipleIcon },
  {
    to: '/mantenedores',
    label: 'Mantenedores',
    desc: 'Catálogos, integraciones y parámetros',
    icon: Settings02Icon,
  },
  { to: '/auditoria', label: 'Auditoría', desc: 'Bitácora de eventos y trazabilidad', icon: Radar01Icon },
];

export const WebLayout = () => {
  const { user, logout, accessibleRoutes, hasAccess } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dynamic filtering only when permissions are loaded
  const usesDynamicNav = accessibleRoutes.length > 0;
  // Rutas de contenido informativo de libre acceso: siempre visibles aunque el
  // backend aún no las incluya en los permisos del usuario.
  const canSee = (to) => PUBLIC_ROUTES.includes(to) || hasAccess(to);
  const filteredValue = usesDynamicNav ? FLYOUT_VALUE.filter((item) => canSee(item.to)) : FLYOUT_VALUE;
  const filteredPortfolio = usesDynamicNav ? FLYOUT_PORTFOLIO.filter((item) => canSee(item.to)) : FLYOUT_PORTFOLIO;
  const filteredGovernance = usesDynamicNav ? FLYOUT_GOVERNANCE.filter((item) => canSee(item.to)) : FLYOUT_GOVERNANCE;
  const filteredAdmin = user?.role === 'admin' ? FLYOUT_ADMIN : [];

  const hasNoAccessibleModules =
    usesDynamicNav &&
    filteredValue.length === 0 &&
    filteredPortfolio.length === 0 &&
    filteredGovernance.length === 0 &&
    filteredAdmin.length === 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[999] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg">
        Saltar al contenido principal
      </a>

      <header className="sticky top-0 z-50 border-b border-white/10 bg-header shadow-md shadow-black/5">
        <nav
          className="max-w-7xl mx-auto flex items-center justify-between px-6 h-14"
          aria-label="Navegación principal">
          <ViaBrandLockup contrast="light" compact asLink />

          <div className="hidden md:flex items-center gap-1">
            <NavItem to="/" label="Inicio" />
            {hasNoAccessibleModules && user ? (
              <span className="text-sm text-muted-foreground px-3 py-2">No tiene módulos asignados</span>
            ) : (
              <>
                {filteredPortfolio.length > 0 && <FlyoutNav label="Portafolio" items={filteredPortfolio} />}
                {filteredValue.length > 0 && <FlyoutNav label="Valor y adopción" items={filteredValue} />}
                {filteredGovernance.length > 0 && <FlyoutNav label="Gobernanza" items={filteredGovernance} />}
                {filteredAdmin.length > 0 && <FlyoutNav label="Administración" items={filteredAdmin} />}
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <CommandSearch />
            </div>
            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-white/10 transition-colors"
                  aria-label="Menú de usuario">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-white/20 text-header text-xs font-bold">
                      {user?.initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden lg:block text-sm font-medium text-header">{user?.name?.split(' ')[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-display font-bold text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.title}</p>
                  <Badge variant="outline" className="mt-2 text-[10px] bg-primary/8 text-primary border-primary/20">
                    {ROLE_LABEL[user?.role]}
                  </Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}>
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden !text-header hover:!text-header hover:bg-white/10 focus:!text-header active:!text-header [&_svg]:size-[22px]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menú"
              aria-expanded={mobileMenuOpen}>
              <HugeiconsIcon
                icon={mobileMenuOpen ? Cancel01Icon : Menu01Icon}
                size={22}
                strokeWidth={2}
                aria-hidden="true"
              />
            </Button>
          </div>
        </nav>

        {mobileMenuOpen && (
          <div className="fixed inset-0 top-[57px] z-40 flex flex-col bg-background md:hidden overflow-hidden">
            <nav className="flex-1 overflow-y-auto px-5 pt-4 pb-8" aria-label="Menú principal móvil">
              {/* Inicio */}
              <NavLink
                to="/"
                end
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors mb-2',
                    isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50',
                  )
                }>
                Inicio
              </NavLink>

              {hasNoAccessibleModules && user ? (
                <p className="text-sm text-muted-foreground px-4 py-6 text-center">
                  No tiene módulos asignados. Contacte al administrador.
                </p>
              ) : (
                <>
                  {filteredPortfolio.length > 0 && (
                    <MobileNavSection
                      label="Portafolio"
                      description="Gestión e iniciativas"
                      items={filteredPortfolio}
                      onNavigate={() => setMobileMenuOpen(false)}
                    />
                  )}
                  {filteredValue.length > 0 && (
                    <MobileNavSection
                      label="Valor y adopción"
                      description="Medir el valor y habilitar a los equipos"
                      items={filteredValue}
                      onNavigate={() => setMobileMenuOpen(false)}
                    />
                  )}
                  {filteredGovernance.length > 0 && (
                    <MobileNavSection
                      label="Gobernanza"
                      description="Control y cumplimiento"
                      items={filteredGovernance}
                      onNavigate={() => setMobileMenuOpen(false)}
                    />
                  )}
                  {filteredAdmin.length > 0 && (
                    <MobileNavSection
                      label="Administración"
                      description="Configuración del sistema"
                      items={filteredAdmin}
                      onNavigate={() => setMobileMenuOpen(false)}
                    />
                  )}
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <InstitutionalBanner className="mt-20" />

      <footer className="border-t border-border/40 bg-card">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="font-display text-sm font-bold text-primary">VIA · Valor de la IA</p>
              <p className="text-[11px] text-muted-foreground">
                Dirección de Innovación y Gobernanza de IA · UC CHRISTUS
              </p>
            </div>
            <div className="flex flex-wrap gap-6 text-xs text-muted-foreground">
              <Link to="/kb" className="hover:text-foreground transition-colors">
                Knowledge Hub
              </Link>
              <Link to="/toolkit/prompts" className="hover:text-foreground transition-colors">
                Toolkit IA
              </Link>
              <Link to="/dashboard" className="hover:text-foreground transition-colors">
                Panel ejecutivo
              </Link>
              <Link to="/registro" className="hover:text-foreground transition-colors">
                Iniciativas
              </Link>
              <Link to="/comite" className="hover:text-foreground transition-colors">
                Gobernanza
              </Link>
              <Link to="/madurez" className="hover:text-foreground transition-colors">
                Madurez
              </Link>
              <Link to="/brand" className="hover:text-foreground transition-colors">
                Identidad
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function FlyoutNav({ label, items }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const panelRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 200);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        className={cn(
          'flex items-center gap-x-1 px-3 py-2 text-sm/6 font-semibold transition-colors rounded-md',
          open ? 'text-header bg-white/10' : 'text-header-muted hover:text-header hover:bg-white/10',
        )}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}>
        {label}
        <svg
          className={cn('size-5 flex-none text-header-muted transition-transform duration-200', open && 'rotate-180')}
          fill="none"
          viewBox="0 0 20 20"
          aria-hidden="true">
          <path
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          className={cn(
            'absolute left-1/2 z-50 mt-3 w-screen -translate-x-1/2 overflow-hidden rounded-3xl bg-card shadow-lg ring-1 ring-border/50',
            items.length > 6 ? 'max-w-2xl' : 'max-w-md',
          )}>
          <div className={cn('p-4', items.length > 6 && 'grid grid-cols-2 gap-x-2')}>
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="group relative flex items-center gap-x-4 rounded-lg p-3 text-sm/6 hover:bg-muted/50 transition-colors">
                <div className="flex size-10 flex-none items-center justify-center rounded-lg bg-muted/50 group-hover:bg-muted">
                  <HugeiconsIcon
                    icon={item.icon}
                    size={22}
                    className="text-muted-foreground group-hover:text-primary transition-colors"
                    aria-hidden="true"
                  />
                </div>
                <div className="flex-auto min-w-0">
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                    {item.label}
                  </p>
                  <p className="mt-0.5 text-muted-foreground text-xs truncate">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'px-3 py-2 text-sm font-semibold transition-colors rounded-md',
          isActive ? 'text-header bg-white/10' : 'text-header-muted hover:text-header hover:bg-white/10',
        )
      }>
      {label}
    </NavLink>
  );
}

function MobileNavItem({ to, label, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive ? 'text-foreground bg-muted/50' : 'text-muted-foreground hover:text-foreground',
        )
      }>
      {label}
    </NavLink>
  );
}

function MobileNavGroup({ label, items, onNavigate }) {
  return (
    <div className="mt-2">
      <p className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className={cn('grid gap-x-1', items.length > 4 ? 'grid-cols-2' : 'grid-cols-1')}>
        {items.map((item) => (
          <MobileNavItem key={item.to} to={item.to} label={item.label} onClick={onNavigate} />
        ))}
      </div>
    </div>
  );
}

function MobileNavSection({ label, description, items, onNavigate }) {
  return (
    <div className="mt-5 first:mt-2">
      {/* Section header */}
      <div className="px-4 py-1.5 mb-1">
        <p className="text-xs font-bold uppercase tracking-wider text-primary">{label}</p>
        {description && <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {/* Section items */}
      <div className="grid grid-cols-1 gap-0.5">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50',
              )
            }>
            <div className="size-8 rounded-lg bg-muted/70 flex items-center justify-center shrink-0">
              <HugeiconsIcon icon={item.icon} size={16} className="text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{item.label}</p>
              <p className="text-[11px] text-muted-foreground truncate">{item.desc}</p>
            </div>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
